// Budget arithmetic for a media plan. Nothing here is stored: it's worked out
// from the plan's lines and the channels they point to every time.
//
//   impressions = budget / CPM × 1000
//
// A line's CPM is its negotiated price if one was entered, otherwise the
// channel's list CPM. There is no plan currency: each line is priced and
// budgeted in its channel's own currency (EUR, USD, TRY, ...), and budgets,
// spend and average CPM are totalled per currency. Impressions don't depend
// on the currency, so their total covers every line.
//
// plan.budgets is the planned total per currency: { EUR: 30000, TRY: 500000 }.
// Plans made before that have a single budget + currency; budgetsOf reads both.

export function budgetsOf(plan) {
  if (plan.budgets) return plan.budgets;
  const single = Number(plan.budget) || 0;
  return single ? { [plan.currency || "EUR"]: single } : {};
}

export function planLines(plan, channelsById) {
  return plan.items.map((item) => {
    const channel = channelsById.get(item.channelId) || null;
    const snapshot = channel || item.snapshot || {};
    const listCpm = channel ? channel.cpm : snapshot.cpm;
    const cpm = item.cpm != null ? item.cpm : listCpm;
    const currency = (channel ? channel.currency : snapshot.currency) || "EUR";
    const budget = Number(item.budget) || 0;
    const priced = cpm != null && cpm > 0;
    return {
      item,
      channel: snapshot,
      missing: !channel,
      listCpm,
      cpm,
      negotiated: item.cpm != null,
      currency,
      budget,
      impressions: priced ? Math.round((budget / cpm) * 1000) : null,
    };
  });
}

// The plan's currencies: its channels' first, then any that only has a budget
export function planCurrencies(plan, lines) {
  return [...new Set([...lines.map((l) => l.currency), ...Object.keys(budgetsOf(plan))])];
}

export function planTotals(plan, lines) {
  const budgets = budgetsOf(plan);
  const byCurrency = planCurrencies(plan, lines).map((currency) => {
    const group = lines.filter((l) => l.currency === currency);
    const allocated = group.reduce((s, l) => s + l.budget, 0);
    const impressions = group.reduce((s, l) => s + (l.impressions || 0), 0);
    const planned = Number(budgets[currency]) || 0;
    return {
      currency,
      channels: group.length,
      planned,
      allocated,
      remaining: planned - allocated,
      impressions,
      // what this currency's spend costs per thousand, weighted by spend
      avgCpm: impressions ? (allocated / impressions) * 1000 : null,
    };
  });
  const territories = new Set(
    lines.flatMap((l) => (l.channel.territories || []).map((t) => t.toLowerCase()))
  );
  return {
    channels: lines.length,
    impressions: lines.reduce((s, l) => s + (l.impressions || 0), 0),
    byCurrency,
    territories: territories.size,
    unpriced: lines.filter((l) => l.budget > 0 && l.impressions == null).length,
  };
}

// A line's share of the spend in its own currency
export function lineShare(line, totals) {
  const group = totals.byCurrency.find((c) => c.currency === line.currency);
  return group && group.allocated ? line.budget / group.allocated : null;
}

// Spreads each currency's planned budget over that currency's lines: evenly,
// or in proportion to reach (lines without a reach figure get the average
// share). Rounded to whole units; the last line of a currency takes the
// rounding difference. Currencies without a planned budget are left as they are.
export function splitBudget(plan, lines, mode) {
  const budgets = budgetsOf(plan);
  const next = new Map();

  planCurrencies(plan, lines).forEach((currency) => {
    const total = Number(budgets[currency]) || 0;
    const group = lines.filter((l) => l.currency === currency);
    if (!group.length || total <= 0) return;

    let weights = group.map(() => 1);
    if (mode === "reach") {
      const reaches = group.map((l) => l.channel.reach || 0);
      const known = reaches.filter(Boolean);
      const avg = known.length ? known.reduce((s, r) => s + r, 0) / known.length : 1;
      weights = reaches.map((r) => r || avg);
    }
    const sum = weights.reduce((s, w) => s + w, 0);

    let given = 0;
    group.forEach((l, i) => {
      const share = i === group.length - 1 ? total - given : Math.round((total * weights[i]) / sum);
      given += share;
      next.set(l.item.channelId, share);
    });
  });

  return plan.items.map((item) =>
    next.has(item.channelId) ? { ...item, budget: next.get(item.channelId) } : item
  );
}

export const formatAmount = (n, currency) =>
  n == null
    ? "—"
    : `${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;

export const formatCount = (n) => (n == null ? "—" : Math.round(n).toLocaleString("en-US"));
